import React, { useState, useEffect } from 'react';

const App = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isVisible, setIsVisible] = useState({});
  const [typedText, setTypedText] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const words = React.useMemo(() => ['a Backend Developer', 'a Cloud Builder', 'a Data Engineer'], []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({ ...prev, [entry.target.id]: entry.isIntersecting }));
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.3 }
    );
    document.querySelectorAll('section[id]').forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const currentWord = words[currentWordIndex];
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      if (charIndex <= currentWord.length) {
        setTypedText(currentWord.slice(0, charIndex));
        charIndex++;
      } else {
        setTimeout(() => {
          const deleteInterval = setInterval(() => {
            if (charIndex > 0) {
              setTypedText(currentWord.slice(0, charIndex));
              charIndex--;
            } else {
              clearInterval(deleteInterval);
              setCurrentWordIndex((prev) => (prev + 1) % words.length);
            }
          }, 45);
        }, 2000);
        clearInterval(typeInterval);
      }
    }, 95);
    return () => clearInterval(typeInterval);
  }, [words, currentWordIndex]);

  const tools = [
    {
      group: 'Languages',
      items: [
        { name: 'Python', tag: 'Flask · FastAPI' },
        { name: 'Go (Golang)', tag: 'Gin' },
        { name: 'SQL', tag: 'PostgreSQL · MySQL' },
      ],
    },
    {
      group: 'Data Engineering',
      items: [
        { name: 'dbt', tag: 'Transformation' },
        { name: 'Airflow', tag: 'Orchestration' },
        { name: 'PostgreSQL', tag: 'Warehousing' },
      ],
    },
    {
      group: 'Infrastructure & DevOps',
      items: [
        { name: 'Docker', tag: 'Containerization' },
        { name: 'CI/CD', tag: 'Pipelines' },
        { name: 'Terraform', tag: 'IaC' },
        { name: 'AWS', tag: 'S3 · DynamoDB · Redshift · Glue' },
      ],
    },
    {
      group: 'Data Analysis',
      items: [
        { name: 'Power BI', tag: 'Dashboards' },
        { name: 'Google Sheets', tag: 'Analysis · Reporting' },
        { name: 'SQL Analytics', tag: 'Query Optimization' },
      ],
    },
    {
      group: 'Testing',
      items: [
        { name: 'Pytest', tag: 'Python Testing' },
      ],
    },
  ];

  const projects = [
    {
      title: 'AI Paraphraser',
      num: '01',
      description: 'AI-powered API that rewrites text while preserving its original meaning, built with FastAPI, PostgreSQL, and NLP models from Hugging Face.',
      tech: ['FastAPI', 'PostgreSQL', 'Docker', 'Anthropic AI API', 'React', 'Vercel'],
      github: 'https://github.com/Fadhili5/paraphraser',
    },
    {
      title: 'Farm-Client App',
      num: '02',
      description: 'Redefines how food is distributed after being produced by the local farmer bridging producer to consumer with clean APIs.',
      tech: ['FastAPI', 'PostgreSQL', 'AWS Boto3', 'Docker'],
      github: 'https://github.com/devharnold/farm-client',
    },
    {
      title: 'Event Management App',
      num: '03',
      description: 'Manages events end-to-end ticketing services, payments via Daraja API, and a clean Spring Boot backend.',
      tech: ['Spring Boot', 'PostgreSQL', 'Daraja API'],
      github: 'https://github.com/devharnold/Eventshub',
    },
    {
      title: 'Leetcode Playground',
      num: '04',
      description: 'My DSA arena where I sharpen algorithmic thinking and problem-solving across Python and SQL.',
      tech: ['Python', 'SQL'],
      github: 'https://github.com/devharnold/dsa-repo',
      live: 'https://leetcode.com/u/devharnold/',
    },
    {
      title: 'Smart Notes API',
      num: '05',
      description: 'A Go service demonstrating full CRUD operations backed by AWS S3, clean architecture, containerized and ready to ship.',
      tech: ['Go', 'Gin', 'Docker', 'AWS S3'],
      github: 'https://github.com/devharnold/smart-notes-api',
    },
    {
      title: 'MarketPulse',
      num: '06',
      description: 'A data engineering pipeline that ingests, transforms, and models stock market data orchestrated end-to-end with clean, testable transformations.',
      tech: ['python', 'dbt', 'Airflow', 'PostgreSQL', 'Docker'],
      github: 'https://github.com/devharnold/marketpulse',
      demoEmail: 'henryarnoldme@gmail.com',
      demoSubject: 'Demo Request: MarketPulse',
      demoBody: "Hi Henry,\n\nI'd like to request a demo of MarketPulse. A bit about what I'm hoping to see:\n\n- \n\nThanks!",
    },
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const buildMailtoHref = (project) => {
    const params = new URLSearchParams({
      subject: project.demoSubject || `Demo Request: ${project.title}`,
      body: project.demoBody || `Hi Henry,\n\nI'd like to request a demo of ${project.title}.\n\nThanks!`,
    });
    return `mailto:${project.demoEmail}?${params.toString()}`;
  };

  return (
    <div style={{ background: '#fff', color: '#000', minHeight: '100vh', fontFamily: '"DM Sans", sans-serif' }}>

      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { overflow-x: hidden; }
        .nav-link { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: #888; text-decoration: none; transition: color 0.2s; background: none; border: none; cursor: pointer; font-family: "DM Sans", sans-serif; }
        .nav-link:hover, .nav-link.active { color: #000; }
        .btn-primary { padding: 14px 32px; background: #000; color: #fff; border: 1px solid #000; font-family: "DM Sans", sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; transition: background 0.2s, color 0.2s; text-decoration: none; display: inline-block; }
        .btn-primary:hover { background: #fff; color: #000; }
        .btn-outline { padding: 14px 32px; background: transparent; color: #000; border: 1px solid #e0e0e0; font-family: "DM Sans", sans-serif; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; transition: border-color 0.2s; text-decoration: none; display: inline-block; }
        .btn-outline:hover { border-color: #000; }
        .tool-item:hover { border-color: #e0e0e0 !important; }
        .project-card:hover { border-color: #000 !important; transform: translateY(-4px); }
        .whatido-card:hover { border-color: #000 !important; transform: translateY(-4px); }
        .whatido-card:hover .card-top-bar { transform: scaleX(1) !important; }
        .social-link:hover { border-color: #000 !important; color: #000 !important; }
        .building-dot { width: 6px; height: 6px; background: #000; border-radius: 50%; animation: pulseDot 1.4s infinite; }
        .typed-cursor { animation: blink 0.9s infinite; }
        .wip-tag { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: #555; border: 1px solid #d0d0d0; padding: 5px 12px; background: #f7f7f7; }
        .stack-chip { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: #555; border: 1px solid #e0e0e0; padding: 4px 10px; transition: border-color 0.2s, color 0.2s; }
        .stack-chip:hover { border-color: #000; color: #000; }
        .demo-btn { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #fff; background: #000; border: 1px solid #000; text-decoration: none; padding: 8px 16px; transition: background 0.2s, color 0.2s; }
        .demo-btn:hover { background: #fff; color: #000; }
        @keyframes pulseDot { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes progressBar { from { width: 0%; } to { width: 51%; } }
        .progress-fill { animation: progressBar 1.8s ease-out forwards; }

        @media (max-width: 768px) {
          .nav-links-wrap { display: none !important; }
          .about-grid { grid-template-columns: 1fr !important; }
          .tools-grid { grid-template-columns: 1fr !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
          .whatido-grid { grid-template-columns: 1fr !important; }
          .building-inner { flex-direction: column !important; gap: 40px !important; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '20px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #e0e0e0', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)',
      }}>
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px' }}>
          &lt;henry /&gt;
        </div>
        <div className="nav-links-wrap" style={{ display: 'flex', gap: 32 }}>
          {['about', 'whatido', 'tools', 'experience', 'building', 'projects', 'contact'].map(s => (
            <button key={s} onClick={() => scrollToSection(s)}
              className={`nav-link${activeSection === s ? ' active' : ''}`}>
              {s === 'whatido' ? 'What I Do' : s === 'building' ? 'Building' : s}
            </button>
          ))}
        </div>
      </nav>

      {/* HERO */}
      <section id="home" style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        padding: '120px 48px 80px', position: 'relative', overflow: 'hidden', background: '#fff',
      }}>
        <div style={{ maxWidth: 900, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'block', width: 40, height: 1, background: '#888' }} />
            Software & Data Engineer
          </div>
          <h1 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(52px, 8vw, 96px)', fontWeight: 800, lineHeight: 0.95, letterSpacing: -3, color: '#000', marginBottom: 24 }}>
            Henry<br />Arnold.
          </h1>
          <p style={{ fontSize: 'clamp(14px, 2vw, 17px)', color: '#555', marginBottom: 48, letterSpacing: '0.01em', lineHeight: 1.7, maxWidth: 520 }}>
            I'm <span style={{ color: '#000' }}>{typedText}</span><span className="typed-cursor">|</span><br />
            Focused on building scalable APIs and cloud-native systems.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <a href="#projects" onClick={e => { e.preventDefault(); scrollToSection('projects'); }} className="btn-primary">View Projects</a>
            <a href="#contact" onClick={e => { e.preventDefault(); scrollToSection('contact'); }} className="btn-outline">Get In Touch</a>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" style={{ padding: '100px 48px', background: '#f7f7f7', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.about ? 1 : 0, transform: isVisible.about ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>01</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>About</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>
        <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 80, alignItems: 'center' }}>
          <div style={{ fontSize: 14, lineHeight: 1.9, color: '#555' }}>
            <p style={{ marginBottom: 20 }}>
              As a <strong style={{ color: '#000', fontWeight: 500 }}>Software & Data Engineer</strong>, I'm passionate about creating new solutions and on a mission to write clean, secure APIs. With experience in <strong style={{ color: '#000', fontWeight: 500 }}>Backend Development</strong>. I work towards solving real problems through innovative, performant software.
            </p>
            <p>
              I'm currently building with keen interest and leaning towards building <strong style={{ color: '#000', fontWeight: 500 }}>data-driven workflows</strong> and actively growing into data engineering.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {[['2+', 'Years exp.'], ['4+', 'Projects'], ['3', 'Languages'], ['∞', 'Curiosity']].map(([num, label]) => (
              <div key={label} style={{ background: '#fff', border: '1px solid #e0e0e0', padding: '28px 24px', textAlign: 'center' }}>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 36, fontWeight: 800, letterSpacing: -2, display: 'block' }}>{num}</span>
                <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#888' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT I DO */}
      <section id="whatido" style={{ padding: '100px 48px', background: '#fff', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.whatido ? 1 : 0, transform: isVisible.whatido ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>02</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>What I Do</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>
        <div className="whatido-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, maxWidth: 860 }}>
          {[
            {
              num: '01 — Core Discipline',
              title: 'API Design &\nDevelopment',
              desc: <>I architect and build <strong style={{ color: '#000', fontWeight: 500 }}>clean, secure RESTful APIs</strong> from schema design to auth flows with production-readiness at every layer. APIs are designed to be <strong style={{ color: '#000', fontWeight: 500 }}>cloud-environment ready</strong>, easily containerized, and deployable to AWS or any cloud provider with minimal friction.</>,
              tag: 'REST · Cloud-Native',
            },
            {
              num: '02 — Core Discipline',
              title: 'Data\nEngineering',
              desc: <>I build <strong style={{ color: '#000', fontWeight: 500 }}>reliable data pipelines</strong> orchestrating ingestion and transformation workflows with <strong style={{ color: '#000', fontWeight: 500 }}>dbt and Airflow</strong>, modeling data in PostgreSQL, and shipping it all containerized on AWS.</>,
              tag: 'dbt · Airflow · AWS',
            },
          ].map((card, i) => (
            <div key={i} className="whatido-card" style={{
              background: '#f7f7f7', padding: '48px 40px',
              border: '1px solid #e0e0e0', position: 'relative',
              overflow: 'hidden', transition: 'border-color 0.3s, transform 0.3s',
            }}>
              <div className="card-top-bar" style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                background: '#000', transform: 'scaleX(0)', transformOrigin: 'left',
                transition: 'transform 0.35s ease',
              }} />
              <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888', marginBottom: 28 }}>{card.num}</div>
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px', color: '#000', marginBottom: 16, lineHeight: 1.2, whiteSpace: 'pre-line' }}>{card.title}</div>
              <div style={{ fontSize: 13, lineHeight: 1.9, color: '#555' }}>{card.desc}</div>
              <span style={{ display: 'inline-block', marginTop: 24, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#888', border: '1px solid #e0e0e0', padding: '5px 12px' }}>{card.tag}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TOOLS */}
      <section id="tools" style={{ padding: '100px 48px', background: '#f7f7f7', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.tools ? 1 : 0, transform: isVisible.tools ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>03</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>Tools & Technologies</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>
        <div className="tools-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>
          <div>
            {tools.slice(0, 2).map((group) => (
              <div key={group.group} style={{ marginBottom: 36 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e0e0e0' }}>{group.group}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {group.items.map(item => (
                    <div key={item.name} className="tool-item" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#fff', border: '1px solid transparent', transition: 'border-color 0.2s' }}>
                      <span style={{ fontSize: 13, color: '#000', letterSpacing: '0.01em' }}>{item.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#888', background: '#f0f0f0', padding: '3px 8px' }}>{item.tag}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div>
            {tools.slice(2).map((group) => (
              <div key={group.group} style={{ marginBottom: 36 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e0e0e0' }}>{group.group}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {group.items.map(item => (
                    <div key={item.name} className="tool-item" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#fff', border: '1px solid transparent', transition: 'border-color 0.2s' }}>
                      <span style={{ fontSize: 13, color: '#000', letterSpacing: '0.01em' }}>{item.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#888', background: '#f0f0f0', padding: '3px 8px' }}>{item.tag}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" style={{ padding: '100px 48px', background: '#fff', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.experience ? 1 : 0, transform: isVisible.experience ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>04</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>Experience</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>
        <div style={{ border: '1px solid #e0e0e0', background: '#f7f7f7', padding: 48, position: 'relative', overflow: 'hidden', maxWidth: 860 }}>
          <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: '#000' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px' }}>Backend Software Engineer</div>
            <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555', background: '#f0f0f0', padding: '6px 14px', border: '1px solid #e0e0e0' }}>2+ Years</div>
          </div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 28, letterSpacing: '0.02em' }}>Specialized in building scalable backend systems and APIs</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              'Designed and developed RESTful APIs using Python (Flask, FastAPI) and Go (Gin) focused on clean contracts, performance, and security.',
              'Implemented cloud infrastructure solutions using AWS services and Infrastructure as Code with Terraform.',
              'Managed PostgreSQL databases with a focus on optimization, query tuning, and performance under load.',
              'Containerized applications using Docker and implemented CI/CD pipelines to streamline deployments.',
              'Conducted ERP system implementations and customizations to align technical systems with business workflows.',
            ].map((point, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, fontSize: 13, color: '#555', lineHeight: 1.7 }}>
                <span style={{ color: '#000', flexShrink: 0, marginTop: 2, fontSize: 10 }}>▸</span>
                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRENTLY BUILDING */}
      <section id="building" style={{ padding: '100px 48px', background: '#f7f7f7', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.building ? 1 : 0, transform: isVisible.building ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>05</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>Currently Building</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>

        <div style={{ border: '1px solid #e0e0e0', background: '#fff', padding: 48, maxWidth: 860, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#000' }} />

          <div className="building-inner" style={{ display: 'flex', gap: 64, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div className="wip-tag">
                  <div className="building-dot" />
                  In Development
                </div>
              </div>

              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 800, letterSpacing: '-0.5px', color: '#000', marginBottom: 16, lineHeight: 1.15 }}>
                AI Travel Planner
              </div>

              <p style={{ fontSize: 13, lineHeight: 1.9, color: '#555', marginBottom: 28 }}>
                An <strong style={{ color: '#000', fontWeight: 500 }}>agentic app</strong> that plans travel end-to-end suggesting flights, hotels, and BnBs for tourists traveling to different countries, generating personalized itineraries, and giving fair, unbiased recommendations. Powered by AI agents that reason across options and adapt plans to the traveler's preferences.
              </p>

              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888', marginBottom: 14 }}>Stack</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['FastAPI', 'Claude AI', 'LangChain', 'Redis', 'React', 'GCP'].map(t => (
                    <span key={t} className="stack-chip">{t}</span>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 28 }}>
                <a href="https://waypoint-plan.vercel.app/" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', textDecoration: 'none', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#000'} onMouseLeave={e => e.target.style.color = '#888'}>
                  ↗ Site
                </a>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888' }}>Progress</div>
                  <div style={{ fontSize: 10, letterSpacing: '0.12em', color: '#555' }}>~51% complete</div>
                </div>
                <div style={{ height: 2, background: '#e0e0e0', width: '100%', position: 'relative' }}>
                  {isVisible.building && (
                    <div className="progress-fill" style={{ height: '100%', background: '#000', width: 0 }} />
                  )}
                </div>
              </div>
            </div>

            <div style={{ width: 220, flexShrink: 0 }}>
              <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e0e0e0' }}>
                Features
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { label: 'Flight suggestions', done: true },
                  { label: 'Hotel Search', done: true },
                  { label: 'Itinerary planning', done: true },
                  { label: 'AI agent reasoning', done: true },
                  { label: 'Insurance & Travel Recommendations', done: true},
                  { label: 'Multi-country support', done: false },
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: f.done ? '#000' : '#aaa', letterSpacing: '0.01em' }}>
                    <span style={{ fontSize: 9, flexShrink: 0, color: f.done ? '#000' : '#ccc' }}>
                      {f.done ? '▸' : '○'}
                    </span>
                    {f.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" style={{ padding: '100px 48px', background: '#fff', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.projects ? 1 : 0, transform: isVisible.projects ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>06</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>Projects</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 2 }}>
          {projects.map((project) => (
            <div key={project.title} className="project-card" style={{ background: '#f7f7f7', border: '1px solid #e0e0e0', padding: 36, transition: 'border-color 0.3s, transform 0.3s' }}>
              <div style={{ fontSize: 10, letterSpacing: '0.15em', color: '#888', marginBottom: 16 }}>Project — {project.num}</div>
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px', color: '#000', marginBottom: 12 }}>{project.title}</div>
              <div style={{ fontSize: 12, lineHeight: 1.8, color: '#555', marginBottom: 20 }}>{project.description}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                {project.tech.map(t => (
                  <span key={t} style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555', border: '1px solid #e0e0e0', padding: '4px 10px' }}>{t}</span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                {project.github && (
                  <a href={project.github} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = '#000'} onMouseLeave={e => e.target.style.color = '#888'}>
                    ↗ GitHub
                  </a>
                )}
                {project.live && (
                  <a href={project.live} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = '#000'} onMouseLeave={e => e.target.style.color = '#888'}>
                    ↗ LeetCode
                  </a>
                )}
                {project.demoEmail && (
                  <a href={buildMailtoHref(project)} className="demo-btn">
                    Request Demo
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" style={{ padding: '100px 48px', background: '#fff', transition: 'opacity 0.8s, transform 0.8s', opacity: isVisible.contact ? 1 : 0, transform: isVisible.contact ? 'translateY(0)' : 'translateY(20px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 64 }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>07</span>
          <h2 style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: -1 }}>Contact</h2>
          <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        </div>
        <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
          <div>
            <p style={{ fontSize: 13, lineHeight: 1.9, color: '#555', marginBottom: 40 }}>
              Have a project in mind? Looking for a backend engineer who cares about clean systems and real performance? Let's talk.
            </p>
            <a href="mailto:henryarnoldme@gmail.com" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(18px, 2.5vw, 26px)', fontWeight: 700, letterSpacing: '-0.5px', color: '#000', textDecoration: 'none', display: 'block', marginBottom: 40, transition: 'opacity 0.2s' }}
              onMouseEnter={e => e.target.style.opacity = '0.5'} onMouseLeave={e => e.target.style.opacity = '1'}>
              henryarnoldme@gmail.com
            </a>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'GitHub', href: 'https://github.com/devharnold' },
                { label: 'LinkedIn', href: 'https://linkedin.com/in/arnold-henry-ah01/' },
                { label: 'Twitter', href: 'https://x.com/rumbleinprod' },
                { label: 'LeetCode', href: 'https://leetcode.com/u/devharnold/' },
              ].map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="social-link"
                  style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#888', textDecoration: 'none', padding: '12px 20px', border: '1px solid #e0e0e0', transition: 'border-color 0.2s, color 0.2s' }}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: -2, color: '#e0e0e0' }}>
              Let's build<br /><span style={{ color: '#000' }}>something</span><br />that lasts.
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '24px 48px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
        <p style={{ fontSize: 11, color: '#888', letterSpacing: '0.08em' }}>© Henry Arnold : Software & Data Engineer</p>
        <p style={{ fontSize: 11, color: '#888', letterSpacing: '0.08em' }}>Have a wonderful {getCurrentDay()} ✨</p>
      </footer>

    </div>
  );
};

export default App;